import CustomFormLabel from "Shared/Components/Inputs/CustomFormLabel";
import StarterKit from "@tiptap/starter-kit";
import {
	MenuButtonBlockquote,
	MenuButtonBold,
	MenuButtonBulletedList,
	MenuButtonCode,
	MenuButtonHorizontalRule,
	MenuButtonItalic,
	MenuButtonOrderedList,
	MenuButtonRedo,
	MenuButtonRemoveFormatting,
	MenuButtonStrikethrough,
	MenuButtonUndo,
	MenuControlsContainer,
	MenuDivider,
	MenuSelectHeading,
	RichTextEditor,
	type RichTextEditorRef,
} from "mui-tiptap";
import { useEffect, useRef } from "react";
import {
	type Control,
	type FieldErrors,
	type FieldPath,
	type FieldValues,
	useController,
} from "react-hook-form";

interface TextEditorProps<FormType extends FieldValues> {
	name: FieldPath<FormType>;
	control: Control<FormType, object>;
	errors: FieldErrors<FormType>;
	label?: string;
}

const TextEditor = <T extends FieldValues>({
	name,
	control,
	label,
}: TextEditorProps<T>) => {
	const editorRef = useRef<RichTextEditorRef>(null);
	const {
		field: { value, onChange },
	} = useController({ name, control });

	useEffect(() => {
		if (editorRef.current) {
			const currentContent = editorRef.current.editor?.getHTML();
			if (currentContent !== value) {
				editorRef.current.editor?.commands.setContent(value || "");
			}
		}
	}, [value]);

	return (
		<>
			{label && <CustomFormLabel>{label}</CustomFormLabel>}
			<RichTextEditor
				ref={editorRef}
				extensions={[StarterKit]}
				content={value}
				onUpdate={({ editor }) => {
					const html = editor.getHTML();
					if (html !== value) onChange(html);
				}}
				renderControls={() => (
					<MenuControlsContainer>
						<MenuSelectHeading />
						<MenuDivider />
						<MenuButtonBold />
						<MenuButtonItalic />
						<MenuButtonStrikethrough />
						<MenuButtonOrderedList />
						<MenuButtonBulletedList />
						<MenuButtonBlockquote />
						<MenuButtonCode />
						<MenuButtonHorizontalRule />
						<MenuDivider />
						<MenuButtonUndo />
						<MenuButtonRedo />
						<MenuButtonRemoveFormatting />
					</MenuControlsContainer>
				)}
			/>
		</>
	);
};

export default TextEditor;
